import CollapsibleActionMenu from '@/components/CollapsibleActionMenu';
import { type ActionMenuButtonProps } from '@/components/CollapsibleActionMenu/ActionMenuButton';
import Dialog from '@/components/Dialog';
import useApi from '@/lib/api/useApi';
import { CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { ListItemIcon } from '@mui/material';
import { useContext, useState } from 'react';
import moment, { type Moment } from 'moment';
import { isEarlier, isLater } from '@/lib/utils/dateUtils';
import { isDateRangeValid, toEndOfDay } from '@/lib/utils/dateRangeUtils';
import {
  type EditorFieldWithoutRelations,
  type EditorSectionWithoutRelations,
} from '@/lib/types';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';
import VisibilityDatePicker from './VisibilityDatePicker';
import DatePickerWrapper from './DatePickerWrapper';
import { FormattedMessage } from 'react-intl';
import ModelType from '@/lib/constants/challengeModel/ModelType.enum';
import FormattedMessageWithChallengeNaming from '@/components/FormattedMessageWithChallengeNaming';
import SelectedModelIdContext from '../../../SelectedModelIdContext';
import ModelsContext from '../../../ModelsContext';

export type VisibilityDatesManagerProps<
  T extends EditorFieldWithoutRelations | EditorSectionWithoutRelations,
> = Omit<ActionMenuButtonProps, 'children'> & {
  variant: 'field' | 'section';
  fieldOrSection: T;
  updateFieldOrSectionInLocalState: (updatedFieldOrSection: T) => void;
};

export default function VisibilityDatesManager<
  T extends EditorFieldWithoutRelations | EditorSectionWithoutRelations,
>({
  variant,
  fieldOrSection,
  updateFieldOrSectionInLocalState,
  ...props
}: VisibilityDatesManagerProps<T>) {
  const api = useApi();

  const [models] = useContext(ModelsContext);
  const selectedModelId = useContext(SelectedModelIdContext);
  const selectedModel = models[selectedModelId];

  const [
    visibleFrom,
    setVisibleFrom,
    visibleFromHasChanged,
    reenableVisibleFromSync,
  ] = useComplexSwrState<Moment | null>(
    fieldOrSection.visibleFrom !== null
      ? moment(fieldOrSection.visibleFrom)
      : null,
    {
      stringifyChangeCheck: true,
    },
  );
  const [
    visibleUntil,
    setVisibleUntil,
    visibleUntilHasChanged,
    reenableVisibleUntilSync,
  ] = useComplexSwrState<Moment | null>(
    fieldOrSection.visibleUntil !== null
      ? moment(fieldOrSection.visibleUntil)
      : null,
    {
      stringifyChangeCheck: true,
    },
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveVisibilityDateChanges = async (): Promise<void> => {
    setSaving(true);
    setDialogOpen(false);

    const updatedFieldOrSection = await api.put<T>(
      `editor/challenge-model/${variant === 'field' ? 'fields' : 'sections'}/${fieldOrSection.id}/visibility-dates`,
      {
        visibleFrom: visibleFrom?.toDate(),
        visibleUntil: toEndOfDay(visibleUntil)?.toDate(),
      },
    );

    updateFieldOrSectionInLocalState(updatedFieldOrSection);

    setSaving(false);

    reenableVisibleFromSync();
    reenableVisibleUntilSync();
  };

  const hasChanged = visibleFromHasChanged || visibleUntilHasChanged;

  return (
    <>
      <CollapsibleActionMenu.Button
        onClick={() => setDialogOpen(true)}
        disabled={saving}
        {...props}
      >
        <ListItemIcon>
          <CalendarIcon />
        </ListItemIcon>
        <FormattedMessage id="editor.model_editor.visibility_dates_manager.label" />
      </CollapsibleActionMenu.Button>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <Dialog.Title>
          <FormattedMessage id="editor.model_editor.visibility_dates_manager.dialog.title" />
        </Dialog.Title>
        <Dialog.Content>
          <Dialog.ContentText>
            <FormattedMessageWithChallengeNaming
              id={`editor.model_editor.visibility_dates_manager.dialog.texts.${selectedModel.type === ModelType.CHALLENGE ? 'challenge' : 'task'}_${variant}`}
            />
          </Dialog.ContentText>
          <DatePickerWrapper>
            <h3>
              <FormattedMessage id="editor.model_editor.visibility_dates_manager.dialog.date_picker.labels.visible_from" />
              :
            </h3>
            <VisibilityDatePicker
              variant="visible_from"
              value={visibleFrom}
              onChange={date => setVisibleFrom(date)}
              onClear={() => setVisibleFrom(null)}
              shouldDisableDate={
                visibleUntil === null
                  ? undefined
                  : date => isLater(date, visibleUntil)
              }
            />
          </DatePickerWrapper>
          <DatePickerWrapper>
            <h3>
              <FormattedMessage id="editor.model_editor.visibility_dates_manager.dialog.date_picker.labels.visible_until" />
              :
            </h3>
            <VisibilityDatePicker
              variant="visible_until"
              value={visibleUntil}
              onChange={date => setVisibleUntil(date)}
              onClear={() => setVisibleUntil(null)}
              shouldDisableDate={
                visibleFrom === null
                  ? undefined
                  : date => isEarlier(date, visibleFrom)
              }
            />
          </DatePickerWrapper>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.Actions.CancelButton onClick={() => setDialogOpen(false)} />
          <Dialog.Actions.SaveButton
            onClick={saveVisibilityDateChanges}
            disabled={
              !hasChanged || !isDateRangeValid(visibleFrom, visibleUntil)
            }
          />
        </Dialog.Actions>
      </Dialog>
    </>
  );
}
